'use client';

import { type FlexProps } from '@ironhack/design-system2/components/flex';
import {
  LinkBox,
  LinkOverlay,
} from '@ironhack/design-system2/components/link-box';
import { SuperCard } from '@ironhack/design-system2/components/supercard';
import { Text } from '@ironhack/design-system2/components/text';
import { render } from 'datocms-structured-text-to-plain-text';
import { findLast, last, propEq } from 'ramda';
import { type ReactElement } from 'react';

import { Button } from '@/app/components/client/button';
import { getHref } from '@/app/components/client/link';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import { type DatoButtonBlock, type DatoCardBlock } from '@/lib/datocms/types';

type ButtonData = Pick<DatoButtonBlock, 'target' | 'variant'>;

const getPrimaryButtonUrl = (buttons: ButtonData[]): string | null => {
  if (buttons.length === 0) return null;

  const primaryButton =
    findLast(propEq('variant', 'primary') as (b: ButtonData) => boolean)(
      buttons
    ) ?? last(buttons);
  if (!primaryButton) return null;

  const url = getHref(primaryButton.target[0]);
  return url;
};

type Props = { card: DatoCardBlock } & FlexProps;

export const Card = (props: Props): ReactElement => {
  const { card, ...flexProps } = props;
  const {
    button: buttons = [],
    content,
    contentAsPlainText = false,
    hexagonLeft,
    hexagonRight,
    leftBadge: leftBadgeBlock,
    logo,
    note,
    maxContentLines,
    media,
    progress,
    rightBadge: rightBadgeBlock,
    size,
    subtitle,
    tags,
    title,
    titleMaxLines,
  } = card;

  const leftBadge = leftBadgeBlock?.[0];
  const rightBadge = rightBadgeBlock?.[0];

  const hasBadges = Boolean(
    hexagonLeft || hexagonRight || leftBadge || rightBadge
  );
  const hasHeader = Boolean(media || hasBadges);
  const hasTags = tags && tags.length > 0;
  const BadgesComponent = media ? SuperCard.MediaBadges : SuperCard.Badges;
  const primaryButtonUrl = getPrimaryButtonUrl(buttons);

  return (
    <SuperCard as={LinkBox} size={size} {...flexProps}>
      {hasHeader && (
        <SuperCard.Header>
          {media && (
            <SuperCard.Media>
              <CmsImage
                data={media.responsiveImage}
                objectFit="cover"
                objectPosition="left"
                sizes="160px"
                style={{ height: '100%' }}
              />
            </SuperCard.Media>
          )}
          {hasBadges && (
            <BadgesComponent
              {...(hexagonLeft && {
                leftHexagon: hexagonLeft.backgroundColor,
              })}
              {...(leftBadge && {
                primaryTag: {
                  text: leftBadge.text,
                  colorScheme: leftBadge.background,
                },
              })}
              {...(hexagonRight && {
                rightHexagon: hexagonRight.backgroundColor,
              })}
              {...(rightBadge && {
                secondaryTag: {
                  text: rightBadge.text,
                  colorScheme: rightBadge.background,
                },
              })}
            />
          )}
        </SuperCard.Header>
      )}
      <SuperCard.Body flexGrow={1}>
        {progress && (
          <SuperCard.Progress
            label={`PROGRESS ${progress}%`}
            value={progress}
          />
        )}
        {title &&
          (primaryButtonUrl ? (
            <LinkOverlay href={primaryButtonUrl} isExternal>
              <SuperCard.Title
                as="h3"
                {...(titleMaxLines && { noOfLines: titleMaxLines })}
              >
                {title}
              </SuperCard.Title>
            </LinkOverlay>
          ) : (
            <SuperCard.Title
              as="h3"
              {...(titleMaxLines && { noOfLines: titleMaxLines })}
            >
              {title}
            </SuperCard.Title>
          ))}
        {subtitle && <SuperCard.Subtitle>{subtitle}</SuperCard.Subtitle>}
        {note && <SuperCard.Note>{note}</SuperCard.Note>}
        {hasTags && <SuperCard.Tags tags={tags.map((t) => t.tag)} />}
        {(content || logo) && (
          <SuperCard.Content gap={2}>
            {logo && <CmsImage data={logo.responsiveImage} />}
            {content &&
              (contentAsPlainText ? (
                <Text noOfLines={maxContentLines}>{render(content)}</Text>
              ) : (
                <StructuredContent
                  data={content}
                  {...(maxContentLines && { noOfLines: maxContentLines })}
                />
              ))}
          </SuperCard.Content>
        )}
      </SuperCard.Body>
      {primaryButtonUrl && (
        // zIndex is needed for the buttons to be over the LinkBox
        <SuperCard.Actions direction="column" gap={2} zIndex={1}>
          {buttons.map((button) => (
            <Button key={button.text} size="s" w="full" {...button} />
          ))}
        </SuperCard.Actions>
      )}
    </SuperCard>
  );
};
